import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Wallet, TrendingUp, TrendingDown, Download, Calendar, CreditCard, Euro, IndianRupee,
  FileText, FileSpreadsheet, Search, Video, Phone, MessageSquare, Building2, Smartphone,
  Receipt, ArrowUpRight, Clock, Users, ArrowUpDown,
} from 'lucide-react';
import {
  Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { useUserCurrency } from '@/hooks/call/useUserCurrency';
import PayoutSetup from './PayoutSetup';
import { exportToPDF, exportToExcel, generateEarningsExportData } from '@/utils/exportUtils';
import { toast } from 'sonner';

// ============== TAX SCENARIOS ==============
// Calculation flow per session:
//   1) GST is deducted from Gross (rate depends on scenario)
//   2) The Net-of-GST is split 50/50 between Platform and Expert
//   3) TDS is deducted from the Expert's 50% to arrive at the final Payout
type ScenarioKey = 'S1' | 'S2' | 'S3' | 'S4';
const SCENARIOS: Record<ScenarioKey, { code: ScenarioKey; label: string; flow: string; gstRate: number; tdsRate: number; gstNote: string; tdsNote: string }> = {
  S1: { code: 'S1', label: 'Indian Expert → Indian User',          flow: 'IN → IN',     gstRate: 0.18,  tdsRate: 0.02,   gstNote: 'GST 18% (CGST+SGST same state / IGST inter-state)', tdsNote: 'Sec 194-O · TDS 2%' },
  S2: { code: 'S2', label: 'Indian Expert → International User',   flow: 'IN → Intl',   gstRate: 0,     tdsRate: 0.02,   gstNote: 'Export of services · 0% under LUT',                  tdsNote: 'Sec 194-O · TDS 2%' },
  S3: { code: 'S3', label: 'International Expert → International', flow: 'Intl → Intl', gstRate: 0,     tdsRate: 0.104,  gstNote: 'Export of services · 0%',                            tdsNote: 'Sec 195 · TDS 10.4%' },
  S4: { code: 'S4', label: 'International Expert → Indian User',   flow: 'Intl → IN',   gstRate: 0.18,  tdsRate: 0.104,  gstNote: 'GST 18% (IGST, place of supply = recipient)',        tdsNote: 'Sec 195 · TDS 10.4%' },
};

const computeBreakdown = (gross: number, scenarioKey: ScenarioKey) => {
  const s = SCENARIOS[scenarioKey];
  const gst = Math.round(gross * s.gstRate);
  const netAfterGst = gross - gst;
  const platformShare = Math.round(netAfterGst * 0.5);
  const expertShare = netAfterGst - platformShare;
  const tds = Math.round(expertShare * s.tdsRate);
  const payout = expertShare - tds;
  return { gst, netAfterGst, platformShare, expertShare, tds, payout };
};

// ============== DUMMY DATA ==============
const monthlyEarnings: Array<{ month: string; sessions: number; gross: number; scenario: ScenarioKey; status: string }> = [
  { month: 'Nov 2024', sessions: 28, gross: 2800, scenario: 'S1', status: 'Paid' },
  { month: 'Dec 2024', sessions: 32, gross: 3200, scenario: 'S2', status: 'Paid' },
  { month: 'Jan 2025', sessions: 26, gross: 2600, scenario: 'S1', status: 'Paid' },
  { month: 'Feb 2025', sessions: 35, gross: 3500, scenario: 'S4', status: 'Paid' },
  { month: 'Mar 2025', sessions: 41, gross: 4100, scenario: 'S1', status: 'Processing' },
  { month: 'Apr 2025', sessions: 23, gross: 2300, scenario: 'S2', status: 'Pending' },
];

const payouts = [
  { id: 'PO-2025-0421', date: 'Apr 21, 2025', method: 'Bank', methodDetail: 'HDFC •••• 4521', amount: 2765, ref: 'TXN8821400321', status: 'Completed' },
  { id: 'PO-2025-0331', date: 'Mar 31, 2025', method: 'UPI', methodDetail: 'expert@okhdfc', amount: 2054, ref: 'TXN8771103456', status: 'Completed' },
  { id: 'PO-2025-0228', date: 'Feb 28, 2025', method: 'Bank', methodDetail: 'HDFC •••• 4521', amount: 2528, ref: 'TXN8654221107', status: 'Completed' },
  { id: 'PO-2025-0501', date: 'May 01, 2025', method: 'Bank', methodDetail: 'HDFC •••• 4521', amount: 3239, ref: '—', status: 'Processing' },
  { id: 'PO-2025-0115', date: 'Jan 15, 2025', method: 'PayPal', methodDetail: 'expert@mail.com', amount: 1240, ref: 'PP-FAILED-991', status: 'Failed' },
];

const transactions: Array<{ id: string; client: string; initials: string; type: string; date: string; duration: string; gross: number; scenario: ScenarioKey; status: string }> = [
  { id: 'TX-9821', client: 'Sarah Wilson',  initials: 'SW', type: 'Video', date: 'Apr 24, 2025 • 4:30 PM',  duration: '45 min', gross: 120, scenario: 'S2', status: 'Completed' },
  { id: 'TX-9820', client: 'Michael Chen',  initials: 'MC', type: 'Audio', date: 'Apr 23, 2025 • 11:00 AM', duration: '30 min', gross: 75,  scenario: 'S2', status: 'Completed' },
  { id: 'TX-9819', client: 'Emma Davis',    initials: 'ED', type: 'Video', date: 'Apr 22, 2025 • 6:15 PM',  duration: '60 min', gross: 150, scenario: 'S2', status: 'Completed' },
  { id: 'TX-9818', client: 'Rohit Sharma',  initials: 'RS', type: 'Chat',  date: 'Apr 21, 2025 • 9:00 AM',  duration: '20 min', gross: 40,  scenario: 'S1', status: 'Refunded' },
  { id: 'TX-9817', client: 'Priya Mehta',   initials: 'PM', type: 'Video', date: 'Apr 20, 2025 • 3:00 PM',  duration: '45 min', gross: 120, scenario: 'S1', status: 'Completed' },
  { id: 'TX-9816', client: 'John Smith',    initials: 'JS', type: 'Audio', date: 'Apr 19, 2025 • 1:30 PM',  duration: '30 min', gross: 75,  scenario: 'S2', status: 'Pending' },
  { id: 'TX-9815', client: 'Aisha Khan',    initials: 'AK', type: 'Video', date: 'Apr 18, 2025 • 5:00 PM',  duration: '60 min', gross: 150, scenario: 'S1', status: 'Completed' },
  { id: 'TX-9814', client: 'David Lee',     initials: 'DL', type: 'Chat',  date: 'Apr 17, 2025 • 10:00 AM', duration: '25 min', gross: 50,  scenario: 'S2', status: 'Completed' },
];

const topServices = [
  { name: 'Therapy Session', sessions: 48, revenue: 5760 },
  { name: 'Career Coaching', sessions: 32, revenue: 3840 },
  { name: 'Life Coaching', sessions: 25, revenue: 2750 },
  { name: 'Wellness Consult', sessions: 18, revenue: 1620 },
];

const countryBreakdown = [
  { name: 'India', value: 42, color: 'hsl(196 94% 67%)' },
  { name: 'United States', value: 23, color: 'hsl(173 52% 66%)' },
  { name: 'United Kingdom', value: 14, color: 'hsl(260 67% 73%)' },
  { name: 'Germany', value: 11, color: 'hsl(43 89% 70%)' },
  { name: 'Others', value: 10, color: 'hsl(220 14% 75%)' },
];

// ============== HELPERS ==============
const statusBadgeClass = (status: string) => {
  switch (status) {
    case 'Paid':
    case 'Completed':
      return 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-emerald-200';
    case 'Processing':
      return 'bg-amber-100 text-amber-700 hover:bg-amber-100 border-amber-200';
    case 'Pending':
      return 'bg-slate-100 text-slate-700 hover:bg-slate-100 border-slate-200';
    case 'Failed':
    case 'Refunded':
      return 'bg-rose-100 text-rose-700 hover:bg-rose-100 border-rose-200';
    default:
      return '';
  }
};

const sessionTypeIcon = (type: string) => {
  if (type === 'Video') return <Video className="h-4 w-4 text-primary" />;
  if (type === 'Audio') return <Phone className="h-4 w-4 text-accent" />;
  return <MessageSquare className="h-4 w-4 text-secondary" />;
};

const payoutMethodIcon = (method: string) => {
  if (method === 'Bank') return <Building2 className="h-4 w-4" />;
  if (method === 'UPI') return <Smartphone className="h-4 w-4" />;
  return <CreditCard className="h-4 w-4" />;
};

const EarningsPage = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('this-month');
  const [selectedYear, setSelectedYear] = useState('2025');
  const [payoutFilter, setPayoutFilter] = useState<'All' | 'Completed' | 'Processing' | 'Failed'>('All');
  const [txFilter, setTxFilter] = useState<'All' | 'Completed' | 'Refunded' | 'Pending'>('All');
  const [txSearch, setTxSearch] = useState('');
  const [showPayoutSetup, setShowPayoutSetup] = useState(false);
  const [hasPayoutMethod, setHasPayoutMethod] = useState(false);
  const { currency } = useUserCurrency();
  const sym = currency === 'INR' ? '₹' : '€';
  const CurrencyIcon = currency === 'INR' ? IndianRupee : Euro;

  // Expert's registration country drives which scenarios apply.
  // INR-registered experts are Indian (scenarios S1 & S2). Others are International (S3 & S4).
  const expertIsIndian = currency === 'INR';
  const expertCountryLabel = expertIsIndian ? 'India' : 'International';
  const applicableScenarioKeys: ScenarioKey[] = expertIsIndian ? ['S1', 'S2'] : ['S3', 'S4'];
  const expertTdsRate = expertIsIndian ? 0.02 : 0.104;
  const expertTdsLabel = expertIsIndian ? 'Sec 194-O · TDS 2%' : 'Sec 195 · TDS 10.4%';

  useEffect(() => {
    const payoutMethod = localStorage.getItem('expertPayoutMethod');
    setHasPayoutMethod(!!payoutMethod);
  }, []);


  const handleExport = (format: 'pdf' | 'excel') => {
    const data = generateEarningsExportData(currency, selectedPeriod);
    if (format === 'pdf') {
      exportToPDF(data);
      toast.success('PDF report downloaded successfully!');
    } else {
      exportToExcel(data);
      toast.success('Excel report downloaded successfully!');
    }
  };

  // Pre-compute per-month breakdown, restricted to scenarios that apply to this expert.
  const monthlyComputed = useMemo(
    () => monthlyEarnings
      .filter((m) => applicableScenarioKeys.includes(m.scenario))
      .map((m) => ({ ...m, ...computeBreakdown(m.gross, m.scenario) })),
    [applicableScenarioKeys]
  );


  // Derived KPIs
  const totals = useMemo(() => {
    const empty = { gross: 0, gst: 0, netAfterGst: 0, platformShare: 0, expertShare: 0, tds: 0, payout: 0, sessions: 0 };
    const totalNet = monthlyComputed.reduce((s, m) => s + m.payout, 0);
    const totalSessions = monthlyComputed.reduce((s, m) => s + m.sessions, 0);
    const thisMonth = monthlyComputed[monthlyComputed.length - 1] ?? empty;
    const lastMonth = monthlyComputed[monthlyComputed.length - 2] ?? empty;
    const delta = lastMonth.payout ? ((thisMonth.payout - lastMonth.payout) / lastMonth.payout) * 100 : 0;
    const pendingPayout = payouts
      .filter((p) => p.status === 'Processing')
      .reduce((s, p) => s + p.amount, 0);
    const totalPaidOut = payouts
      .filter((p) => p.status === 'Completed')
      .reduce((s, p) => s + p.amount, 0);
    return { totalNet, totalSessions, thisMonth, lastMonth, delta, pendingPayout, totalPaidOut };
  }, [monthlyComputed]);

  const trendData = monthlyComputed.map((m) => ({ name: m.month.split(' ')[0], amount: m.payout }));
  const topServicesTotal = topServices.reduce((s, t) => s + t.revenue, 0);

  const filteredPayouts = payouts.filter((p) => payoutFilter === 'All' || p.status === payoutFilter);
  const filteredTx = transactions
    .filter((t) => applicableScenarioKeys.includes(t.scenario))
    .filter((t) => {
      const matchesFilter = txFilter === 'All' || t.status === txFilter;
      const matchesSearch = t.client.toLowerCase().includes(txSearch.toLowerCase()) ||
                            t.id.toLowerCase().includes(txSearch.toLowerCase());
      return matchesFilter && matchesSearch;
    });


  if (showPayoutSetup) {
    return <PayoutSetup onBack={() => setShowPayoutSetup(false)} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-3xl font-bold">Earnings & Payouts</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Track your income, view statements and manage payouts.
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this-week">This Week</SelectItem>
              <SelectItem value="this-month">This Month</SelectItem>
              <SelectItem value="last-month">Last Month</SelectItem>
              <SelectItem value="this-year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Download className="h-4 w-4 mr-2" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('pdf')}>
                <FileText className="h-4 w-4 mr-2" /> Download PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('excel')}>
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Download Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="overflow-hidden border-0 text-primary-foreground shadow-md"
          style={{ background: 'var(--gradient-primary)' }}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wide opacity-90">Total Earnings</span>
              <CurrencyIcon className="h-5 w-5 opacity-90" />
            </div>
            <div className="text-3xl font-bold">{sym}{totals.totalNet.toLocaleString()}</div>
            <div className="flex items-center text-xs mt-1 opacity-90">
              <TrendingUp className="h-3 w-3 mr-1" /> Lifetime net earnings
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">This Month</span>
              <Calendar className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold">{sym}{totals.thisMonth.payout.toLocaleString()}</div>
            <div className={`flex items-center text-xs mt-1 ${totals.delta >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {totals.delta >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
              {totals.delta.toFixed(1)}% vs last month
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Pending Payout</span>
              <Wallet className="h-5 w-5 text-amber-500" />
            </div>
            <div className="text-2xl font-bold">{sym}{totals.pendingPayout.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground mt-1">Next payout: May 15, 2025</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Total Sessions</span>
              <Users className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="text-2xl font-bold">{totals.totalSessions}</div>
            <div className="text-xs text-muted-foreground mt-1">Across last 6 months</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 lg:w-auto h-auto gap-1 p-1">
          <TabsTrigger value="overview" className="py-2">Overview</TabsTrigger>
          <TabsTrigger value="statement" className="py-2">Earnings Statement</TabsTrigger>
          <TabsTrigger value="payouts" className="py-2">Payout Statement</TabsTrigger>
          <TabsTrigger value="transactions" className="py-2">Transactions</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Earnings Trend</CardTitle>
                <CardDescription>Net earnings over the last 6 months</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false}
                      tickFormatter={(v) => `${sym}${v}`} />
                    <Tooltip
                      contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8 }}
                      formatter={(v: number) => [`${sym}${v.toLocaleString()}`, 'Net']}
                    />
                    <Bar dataKey="amount" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Earnings by Country of Users</CardTitle>
                <CardDescription>Revenue distribution by user country</CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  const OTHERS_THRESHOLD = 8;
                  const majors = countryBreakdown.filter((c) => c.value >= OTHERS_THRESHOLD && c.name !== 'Others');
                  const minorsTotal = countryBreakdown
                    .filter((c) => c.value < OTHERS_THRESHOLD || c.name === 'Others')
                    .reduce((s, c) => s + c.value, 0);
                  const chartData = minorsTotal > 0
                    ? [...majors, { name: 'Others', value: minorsTotal, color: 'hsl(220 14% 75%)' }]
                    : majors;
                  return (
                    <>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie data={chartData} dataKey="value" nameKey="name"
                            innerRadius={50} outerRadius={80} paddingAngle={2}>
                            {chartData.map((c, i) => <Cell key={i} fill={c.color} />)}
                          </Pie>
                          <Tooltip formatter={(v: number) => `${v}%`} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 mt-3 text-xs">
                        {chartData.map((c) => (
                          <div key={c.name} className="flex items-center gap-2 min-w-0">
                            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: c.color }} />
                            <span className="truncate text-muted-foreground">{c.name}</span>
                            <span className="ml-auto font-medium tabular-nums">{c.value}%</span>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top Performing Services</CardTitle>
              <CardDescription>Revenue contribution by service</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {topServices.map((s) => {
                const pct = (s.revenue / topServicesTotal) * 100;
                return (
                  <div key={s.name} className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <div>
                        <span className="font-medium">{s.name}</span>
                        <span className="text-muted-foreground ml-2">• {s.sessions} sessions</span>
                      </div>
                      <div className="font-semibold">
                        {sym}{s.revenue.toLocaleString()}
                        <span className="text-muted-foreground font-normal ml-2">{pct.toFixed(0)}%</span>
                      </div>
                    </div>
                    <Progress value={pct} className="h-2" />
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* EARNINGS STATEMENT */}
        <TabsContent value="statement" className="space-y-4 mt-6">
          {/* Calculation formula card */}
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="text-base">How your earnings are calculated</CardTitle>
                  <CardDescription>
                    Based on your registered country ({expertCountryLabel}), the rules below apply to every session.
                  </CardDescription>
                </div>
                <Badge variant="outline" className="font-medium">Registered in {expertCountryLabel}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="px-2.5 py-1 rounded-md bg-background border font-medium">Gross (user paid)</span>
                <span className="text-muted-foreground">−</span>
                <span className="px-2.5 py-1 rounded-md bg-rose-50 border border-rose-200 text-rose-700">GST</span>
                <span className="text-muted-foreground">=</span>
                <span className="px-2.5 py-1 rounded-md bg-background border font-medium">Net of GST</span>
                <span className="text-muted-foreground">×</span>
                <span className="px-2.5 py-1 rounded-md bg-background border">50% Platform</span>
                <span className="text-muted-foreground">+</span>
                <span className="px-2.5 py-1 rounded-md bg-background border font-medium">50% Expert</span>
                <span className="text-muted-foreground">−</span>
                <span className="px-2.5 py-1 rounded-md bg-rose-50 border border-rose-200 text-rose-700">TDS ({(expertTdsRate * 100).toFixed(1)}%)</span>
                <span className="text-muted-foreground">=</span>
                <span className="px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 font-semibold">Your earnings</span>
              </div>
              <div className="mt-4">
                <div className="text-xs font-medium text-muted-foreground mb-2">Applicable cases for you</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {applicableScenarioKeys.map((key) => {
                    const s = SCENARIOS[key];
                    return (
                      <div key={s.code} className="flex items-start gap-3 p-3 rounded-md bg-background border">
                        <Badge variant="outline" className="font-mono">{s.code}</Badge>
                        <div className="text-xs space-y-0.5 min-w-0">
                          <div className="font-medium text-foreground truncate">{s.label}</div>
                          <div className="text-muted-foreground">{s.gstNote}</div>
                          <div className="text-muted-foreground">{s.tdsNote}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>


          <Card>
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <CardTitle className="text-base">Monthly Earnings Statement</CardTitle>
                <CardDescription>Detailed breakdown by tax scenario</CardDescription>
              </div>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2024">2024</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead><span className="inline-flex items-center gap-1">Month <ArrowUpDown className="h-3 w-3" /></span></TableHead>
                      <TableHead className="text-right">Sessions</TableHead>
                      <TableHead>Scenario</TableHead>
                      <TableHead className="text-right">Gross</TableHead>
                      <TableHead className="text-right">GST</TableHead>
                      <TableHead className="text-right">Net of GST</TableHead>
                      <TableHead className="text-right">Platform 50%</TableHead>
                      <TableHead className="text-right">Expert 50%</TableHead>
                      <TableHead className="text-right">TDS</TableHead>
                      <TableHead className="text-right">Payout</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthlyComputed.map((m) => (
                      <TableRow key={m.month}>
                        <TableCell className="font-medium whitespace-nowrap">{m.month}</TableCell>
                        <TableCell className="text-right">{m.sessions}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1.5">
                            <Badge variant="outline" className="font-mono text-[10px] px-1.5">{m.scenario}</Badge>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">{SCENARIOS[m.scenario].flow}</span>
                          </span>
                        </TableCell>
                        <TableCell className="text-right">{sym}{m.gross.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-rose-600 whitespace-nowrap">
                          -{sym}{m.gst.toLocaleString()}
                          <span className="text-[10px] text-muted-foreground ml-1">({(SCENARIOS[m.scenario].gstRate * 100).toFixed(0)}%)</span>
                        </TableCell>
                        <TableCell className="text-right">{sym}{m.netAfterGst.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{sym}{m.platformShare.toLocaleString()}</TableCell>
                        <TableCell className="text-right">{sym}{m.expertShare.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-rose-600 whitespace-nowrap">
                          -{sym}{m.tds.toLocaleString()}
                          <span className="text-[10px] text-muted-foreground ml-1">({(SCENARIOS[m.scenario].tdsRate * 100).toFixed(1)}%)</span>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-primary">{sym}{m.payout.toLocaleString()}</TableCell>
                        <TableCell><Badge variant="outline" className={statusBadgeClass(m.status)}>{m.status}</Badge></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm">
                            <Download className="h-4 w-4 mr-1" /> PDF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell className="font-semibold">Total</TableCell>
                      <TableCell className="text-right font-semibold">{totals.totalSessions}</TableCell>
                      <TableCell />
                      <TableCell className="text-right font-semibold">{sym}{monthlyComputed.reduce((s,m)=>s+m.gross,0).toLocaleString()}</TableCell>
                      <TableCell className="text-right font-semibold">-{sym}{monthlyComputed.reduce((s,m)=>s+m.gst,0).toLocaleString()}</TableCell>
                      <TableCell className="text-right font-semibold">{sym}{monthlyComputed.reduce((s,m)=>s+m.netAfterGst,0).toLocaleString()}</TableCell>
                      <TableCell className="text-right font-semibold">{sym}{monthlyComputed.reduce((s,m)=>s+m.platformShare,0).toLocaleString()}</TableCell>
                      <TableCell className="text-right font-semibold">{sym}{monthlyComputed.reduce((s,m)=>s+m.expertShare,0).toLocaleString()}</TableCell>
                      <TableCell className="text-right font-semibold">-{sym}{monthlyComputed.reduce((s,m)=>s+m.tds,0).toLocaleString()}</TableCell>
                      <TableCell className="text-right font-bold text-primary">{sym}{totals.totalNet.toLocaleString()}</TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>


        {/* PAYOUT STATEMENT */}
        <TabsContent value="payouts" className="space-y-4 mt-6">
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="text-base">How your payout is calculated</CardTitle>
                  <CardDescription>
                    Your 50% expert share from each session is held, then TDS is deducted to arrive at the final payout.
                  </CardDescription>
                </div>
                <Badge variant="outline" className="font-medium">{expertTdsLabel}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="px-2.5 py-1 rounded-md bg-background border font-medium">Expert Share (50% of Net of GST)</span>
                <span className="text-muted-foreground">−</span>
                <span className="px-2.5 py-1 rounded-md bg-rose-50 border border-rose-200 text-rose-700">
                  TDS ({(expertTdsRate * 100).toFixed(1)}%)
                </span>
                <span className="text-muted-foreground">=</span>
                <span className="px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 font-semibold">
                  Final Payout (credited to you)
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                TDS is withheld and remitted to the tax authority on your behalf. You can use it for your tax filing.
              </p>
            </CardContent>
          </Card>


          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Total Paid Out</div>
                <div className="text-2xl font-bold">{sym}{totals.totalPaidOut.toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Processing</div>
                <div className="text-2xl font-bold text-amber-600">{sym}{totals.pendingPayout.toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Next Scheduled</div>
                <div className="text-2xl font-bold">May 15, 2025</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-base">Payout History</CardTitle>
                  <CardDescription>All payouts sent to your linked methods</CardDescription>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {(['All', 'Completed', 'Processing', 'Failed'] as const).map((f) => (
                    <Button
                      key={f}
                      variant={payoutFilter === f ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setPayoutFilter(f)}
                    >
                      {f}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Payout ID</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead className="text-right">Expert Share</TableHead>
                      <TableHead className="text-right">TDS ({(expertTdsRate * 100).toFixed(1)}%)</TableHead>
                      <TableHead className="text-right">Final Payout</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Receipt</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPayouts.map((p) => {
                      const grossShare = Math.round(p.amount / (1 - expertTdsRate));
                      const tdsAmt = grossShare - p.amount;
                      return (
                        <TableRow key={p.id}>
                          <TableCell className="font-mono text-xs">{p.id}</TableCell>
                          <TableCell className="whitespace-nowrap">{p.date}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center">
                                {payoutMethodIcon(p.method)}
                              </div>
                              <div>
                                <div className="text-sm font-medium">{p.method}</div>
                                <div className="text-xs text-muted-foreground">{p.methodDetail}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{sym}{grossShare.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-rose-600 whitespace-nowrap">-{sym}{tdsAmt.toLocaleString()}</TableCell>
                          <TableCell className="text-right font-semibold text-primary">{sym}{p.amount.toLocaleString()}</TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">{p.ref}</TableCell>
                          <TableCell><Badge variant="outline" className={statusBadgeClass(p.status)}>{p.status}</Badge></TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" disabled={p.status !== 'Completed'}>
                              <Receipt className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {filteredPayouts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                          No payouts match this filter.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>


        {/* TRANSACTIONS */}
        <TabsContent value="transactions" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <CardTitle className="text-base">Session Transactions</CardTitle>
                  <CardDescription>Per-session earnings ledger</CardDescription>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <div className="relative flex-1 sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search client or ID..."
                      value={txSearch}
                      onChange={(e) => setTxSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Select value={txFilter} onValueChange={(v: any) => setTxFilter(v)}>
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="All">All Status</SelectItem>
                      <SelectItem value="Completed">Completed</SelectItem>
                      <SelectItem value="Refunded">Refunded</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {filteredTx.map((t) => {
                const b = computeBreakdown(t.gross, t.scenario);
                const s = SCENARIOS[t.scenario];
                return (
                  <div key={t.id} className="flex flex-col gap-3 p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="h-11 w-11 rounded-full flex items-center justify-center text-primary-foreground font-semibold"
                          style={{ background: 'var(--gradient-primary)' }}>
                          {t.initials}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{t.client}</span>
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                              {sessionTypeIcon(t.type)} {t.type}
                            </span>
                            <Badge variant="outline" className="font-mono text-[10px] px-1.5">{t.scenario} · {s.flow}</Badge>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5 flex-wrap">
                            <span>{t.id}</span>
                            <span>•</span>
                            <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" />{t.date}</span>
                            <span>•</span>
                            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{t.duration}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="text-right">
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Payout</div>
                          <div className="font-semibold text-primary flex items-center gap-1">
                            {sym}{b.payout.toLocaleString()} <ArrowUpRight className="h-3 w-3 text-emerald-500" />
                          </div>
                        </div>
                        <Badge variant="outline" className={statusBadgeClass(t.status)}>{t.status}</Badge>
                      </div>
                    </div>
                    {/* Breakdown strip */}
                    <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 pt-2 border-t text-xs">
                      <div>
                        <div className="text-muted-foreground">Gross</div>
                        <div className="font-medium">{sym}{t.gross.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">GST ({(s.gstRate * 100).toFixed(0)}%)</div>
                        <div className="text-rose-600">-{sym}{b.gst.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Net of GST</div>
                        <div className="font-medium">{sym}{b.netAfterGst.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Expert 50%</div>
                        <div className="font-medium">{sym}{b.expertShare.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">TDS ({(s.tdsRate * 100).toFixed(1)}%)</div>
                        <div className="text-rose-600">-{sym}{b.tds.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Payout</div>
                        <div className="font-semibold text-primary">{sym}{b.payout.toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {filteredTx.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No transactions match your search.
                </div>
              )}
              <div className="text-center pt-2">
                <Button variant="outline" size="sm">Load more</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payout Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center text-base">
            <CreditCard className="h-5 w-5 mr-2" />
            Payout Settings
          </CardTitle>
          <CardDescription>Set up your payment preferences for {currency} payments</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {hasPayoutMethod ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">Payout method configured</h4>
                    <p className="text-sm text-muted-foreground">Ready to receive payments in {currency}</p>
                  </div>
                </div>
                <Badge variant="secondary">Active</Badge>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowPayoutSetup(true)}>Update Method</Button>
                <Button>Request Payout</Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">Configure your payout settings to receive earnings in {currency}</p>
              <Button onClick={() => setShowPayoutSetup(true)}>
                <Wallet className="h-4 w-4 mr-2" />
                Setup Payout Method
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EarningsPage;
